import { useMemo, useState } from 'react';
import { useUser } from '../UserProvider';
import { useAppConfig } from '../AppConfigProvider';
import qs from 'qs';

// use this for things that are triggered by things like clicking a button to create a record
export function useLazyFetch<T>(path?: string | null, requiresAuth = true) {
  const appConfig = useAppConfig();
  const [, { getAuthToken }] = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useMemo(
    () =>
      async (
        init?: Omit<RequestInit, 'body'> & {
          body?: Record<string, unknown>;
          queryParams?: Record<string, unknown>;
        }
      ) => {
        try {
          if (!path) {
            throw new Error('Fetch path has not been defined');
          }

          setError(null);
          setIsLoading(true);

          const url = new URL(path, appConfig.data?.apiUrl);
          if (init?.queryParams) {
            const additionalParams = qs.stringify(init.queryParams, {
              skipNulls: true,
            });
            url.search = url.search
              ? `${url.search}&${additionalParams}`
              : `?${additionalParams}`;
          }

          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
          };

          if (requiresAuth) {
            const authToken = await getAuthToken();
            headers['Authorization'] = `Bearer ${authToken}`;
          }

          const response = await fetch(path, {
            mode: 'cors',
            cache: 'no-cache',
            ...init,
            //FIXME: this content-type = application/json and the stringify body aren't working.  need to investigate.  Results in empty body and need to handle the header and stringify on the caller side for now
            body: init?.body ? JSON.stringify(init.body) : undefined,
          });
          if (response.status >= 400) {
            throw new Error(`Failed request, status=${response.status}`);
          }

          const data: T = await response.json();
          return data;
        } catch (err) {
          console.error('Failed to fetch', err);
          setError('Failed to execute request');
          return null;
        } finally {
          setIsLoading(false);
        }
      },
    [path, appConfig, requiresAuth, getAuthToken]
  );

  return [execute, isLoading, error] as const;
}

export default useLazyFetch;
